import type { AnalyticsClient } from '@/scopes/common/domain/types'
import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types'
import type { PriceTableAnalyticsWithBrand } from '@/scopes/prices/domain/types'
import {
  isAccommodationAvailableSelector,
  transportTypeFilterOptionsSelector,
} from '@/scopes/prices/components/selectors'
import {
  createAdditionalDetailsEvent,
  createFlightSelectedEvent,
  createParticipantsAllocatedEvent,
  createPricesViewedEvent,
} from '@/scopes/prices/domain/analytics'
import { FilterSelection, FiltersInPricesValues, PricesFilterType } from '@/scopes/prices/domain/filters/types'
import { RoomInfo } from '@/scopes/prices/domain/rooms/types'
import { FlightType } from '@/scopes/prices/domain/transports/types'
import { IsAccoAvailableApiClient } from '@/scopes/prices/infrastructure/availability/availabilityApiClient'
import { FiltersApiClient } from '@/scopes/prices/infrastructure/filters/filtersAPIClient'
import { PricesApiClient } from '@/scopes/prices/infrastructure/pricesByDuration/pricesAPIClient'
import { PromotedPriceApiClient } from '@/scopes/prices/infrastructure/promoted-price/promotedPriceApiClient'
import { RoomSelectorApiClient } from '@/scopes/prices/infrastructure/rooms/roomSelectorApiClient'
import {
  emptyRoomsResponse,
  emptySelfDriveResponse,
  emptyTransportResponse,
  PricesStoreApi,
} from '@/scopes/prices/infrastructure/store'
import { FlightSelectorApiClient } from '@/scopes/prices/infrastructure/transport/flight/flightSelectorApiClient'
import { SelfDriveSelectorApiClient } from '@/scopes/prices/infrastructure/transport/selfdrive/selfDriveSelectorApiClient'
import { QueryParametersToStateMapper } from '@/scopes/prices/infrastructure/types'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'

export function pricesUseCases({
  filtersApiClient,
  pricesApiClient,
  roomSelectorApiClient,
  flightSelectorApiClient,
  selfDriveSelectorApiClient,
  promotedPriceAPIClient,
  isAccoAvailableApiClient,
  analyticsClient,
  store,
  queryParametersToStateMapper,
  queryStringSerializer,
  browserQueryString,
}: {
  filtersApiClient: FiltersApiClient
  pricesApiClient: PricesApiClient
  roomSelectorApiClient: RoomSelectorApiClient
  flightSelectorApiClient: FlightSelectorApiClient
  selfDriveSelectorApiClient: SelfDriveSelectorApiClient
  promotedPriceAPIClient: PromotedPriceApiClient
  isAccoAvailableApiClient: IsAccoAvailableApiClient
  analyticsClient: AnalyticsClient
  store: PricesStoreApi
  queryParametersToStateMapper: QueryParametersToStateMapper<{ filterSelection: FilterSelection }, FilterSelection>
  queryStringSerializer: QueryStringSerializer<FilterSelection>
  browserQueryString: BrowserQueryString
}) {
  function updateQueryString(state: { filterSelection: FilterSelection }) {
    const queryParameters = queryParametersToStateMapper.toParameters(state)
    const queryString = queryStringSerializer.serialize(queryParameters)
    browserQueryString.set(queryString)
  }

  async function initPrices({
    accoId,
    bookingGateId,
    contextItemId,
    analytics,
    initialFilterSelection,
    promotedPriceId,
  }: {
    accoId: string
    contextItemId: string
    bookingGateId: string
    analytics: PriceTableAnalyticsWithBrand | null
    initialFilterSelection: FilterSelection
    promotedPriceId: string
  }) {
    const { setIn } = store.getState()
    const initialFilters = await getInitialFilters(initialFilterSelection)
    setIn('accoId', accoId)
    setIn('contextItemId', contextItemId)
    setIn('bookingGateId', bookingGateId)
    setIn('analytics', analytics)
    setIn('filterSelection', initialFilters)
    setIn('promotedPriceId', promotedPriceId)
    const isAccoAvailable = await isAccommodationAvailable()
    if (isAccoAvailable) {
      await getPromotedPrice()
    }
  }

  async function getInitialFilters(initialFilterSelection: FilterSelection) {
    const queryString = browserQueryString.get()
    if (!queryString) {
      return initialFilterSelection
    }
    const queryParameters = queryStringSerializer.deserialize(queryString)
    const state = queryParametersToStateMapper.toState(queryParameters)
    return state?.filterSelection || initialFilterSelection
  }

  async function initPriceTable() {
    await getFilters()
    await getPrices()
  }

  async function getFilters() {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection } = store.getState()
    setIn('getFilters.loading', true)
    try {
      const filtersResult = await filtersApiClient.getFilters({
        accoId,
        contextItemId,
        bookingGateId,
        filterSelection,
      })
      setIn('getFilters.loading', false)
      setIn('getFilters.isEmpty', filtersResult.isEmpty)
      setIn('filterOptions', filtersResult)
      setIn('filterSelection', filtersResult.filterSelection)
    }
    catch (error) {
      setIn('getFilters.loading', false)
      setIn('getFilters.error', true)
      throw error
    }
  }

  async function sendViewPricesEvent() {
    const { analytics, filterSelection, promotedPriceResponse } = store.getState()
    if (!analytics)
      return

    const { accoId, accoName, accoBrand, breadcrumbs, stars, rating, cityId, regionId, countryId } = analytics
    const { TransportType, Participants, Mealplan, Duration } = filterSelection
    const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
      Participants || [],
    )

    analyticsClient.sendEvent(
      createPricesViewedEvent({
        accoId: `${accoId}`,
        accoBrand,
        rating,
        accoName,
        accoCategory: breadcrumbs,
        accoStars: stars,
        transport: TransportType ?? '',
        boardType: Mealplan ?? '',
        durations: Duration?.join(',') ?? '',
        partyComposition,
        departureDate: promotedPriceResponse?.departureDate.date ?? '',
        entryPrice: promotedPriceResponse?.price.value ?? 0,
        collection: '',
        roomType: '',
        totalGuests,
        cityId,
        regionId,
        countryId,
        originList: analytics?.originList ?? '',
      }),
    )
  }

  async function getPrices() {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection } = store.getState()
    setIn('getPrices.loading', true)
    try {
      const filtersCriteria = { contextItemId, bookingGateId, accoId, filterSelection }
      const pricesResult = await pricesApiClient.getPrices(filtersCriteria)
      setIn('pricesResponse', pricesResult)
      setIn('getPrices.loading', false)
      await sendViewPricesEvent()
    }
    catch (error) {
      setIn('getPrices.loading', false)
      setIn('getPrices.error', true)
      throw error
    }
  }

  async function getRooms() {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection, selectedPriceId } = store.getState()
    setIn('getRooms.loading', true)
    try {
      const filtersCriteria = {
        contextItemId,
        bookingGateId,
        accoId,
        filterSelection,
        selectedPriceId: selectedPriceId || '',
      }
      const roomsResult = await roomSelectorApiClient.getRooms(filtersCriteria)
      setIn('roomsResponse', roomsResult)
      setIn('getRooms.loading', false)
    }
    catch (error) {
      setIn('getRooms.loading', false)
      setIn('getRooms.error', true)
      throw error
    }
  }

  const sendAdditionalDetailsEvent = () => {
    const { analytics, promotedPriceResponse, filterSelection } = store.getState()
    if (!analytics)
      return

    const { accoId, cityId, regionId, countryId } = analytics
    const { TransportType, Participants, Mealplan } = filterSelection
    const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
      Participants || [],
    )
    analyticsClient.sendEvent(
      createAdditionalDetailsEvent({
        accoId: `${accoId}`,
        transport: TransportType ?? '',
        boardType: Mealplan ?? '',
        duration: promotedPriceResponse?.duration ? `${promotedPriceResponse.duration}` : '',
        partyComposition,
        totalGuests,
        cityId,
        regionId,
        countryId,
        departureDate: promotedPriceResponse?.departureDate ? `${promotedPriceResponse.departureDate.date}` : '',
        entryPrice: promotedPriceResponse?.price ? `${promotedPriceResponse.price.value}` : '',
        campaignId: promotedPriceResponse?.campaignId || '',
      }),
    )
  }

  async function getPromotedPrice() {
    const { setIn, accoId, contextItemId, filterSelection, promotedPriceId, isAccommodationAvailableResponse }
      = store.getState()
    setIn('getPromotedPrice.loading', true)
    try {
      const transportType = filterSelection?.TransportType || isAccommodationAvailableResponse.transportTypes[0]

      const newFilterSelection = {
        ...filterSelection,
        ...(transportType && { TransportType: transportType }),
      }
      const filtersCriteria = { contextItemId, accoId, filterSelection: newFilterSelection, promotedPriceId }
      const promotedPriceResult = await promotedPriceAPIClient.getPromotedPrice(filtersCriteria)
      setIn('promotedPriceResponse', promotedPriceResult)
      setIn('getPromotedPrice.loading', false)
      setIn('getPromotedPrice.success', true)
      sendAdditionalDetailsEvent()
    }
    catch (error) {
      setIn('getPromotedPrice.loading', false)
      setIn('getPromotedPrice.error', true)
      throw error
    }
  }

  async function isAccommodationAvailable() {
    const { setIn, accoId, contextItemId, bookingGateId } = store.getState()
    setIn('isAccommodationAvailable.loading', true)
    try {
      const isAccommodationAvailableResult = await isAccoAvailableApiClient.isAccommodationAvailable({
        contextItemId,
        accoId,
        bookingGateId,
      })
      setIn('isAccommodationAvailableResponse', isAccommodationAvailableResult)
      setIn('isAccommodationAvailable.loading', false)
      setIn('isAccommodationAvailable.success', true)
      return isAccommodationAvailableResult.isAccommodationAvailable
    }
    catch (error) {
      setIn('isAccommodationAvailable.loading', false)
      setIn('isAccommodationAvailable.error', true)
      throw error
    }
  }

  async function setOutboundRouteId(routeId: string | null) {
    const { setIn, outboundRoutes } = store.getState()
    if (!routeId) {
      setIn('selectedOutboundRouteId', null)
      setIn('selectedInboundRouteId', null)
      setIn('inboundRoutes', [])
      return
    }
    const outboundRoute = outboundRoutes.find(route => route.id === routeId)
    setIn('selectedOutboundRouteId', routeId)
    const relatedInboundRoutes = outboundRoute?.relatedRoutes
    const selectedIInboundRouteId = relatedInboundRoutes?.[0]?.id || null
    setIn('selectedInboundRouteId', selectedIInboundRouteId)
    setIn('inboundRoutes', relatedInboundRoutes || [])
    await sendFlightEvents({ outbound: routeId, inbound: selectedIInboundRouteId })
  }

  async function setInboundRouteId(routeId: string | null) {
    const { setIn } = store.getState()
    setIn('selectedInboundRouteId', routeId)
    await sendFlightEvents({ inbound: routeId })
  }

  async function sendFlightEvents({ outbound, inbound }: { outbound?: string | null, inbound?: string | null }) {
    const { inboundRoutes, outboundRoutes } = store.getState()
    const outboundRoute = outboundRoutes.find(route => route.id === outbound) || null
    const inboundRoute = inboundRoutes.find(route => route.id === inbound) || null

    if (outboundRoute) {
      analyticsClient.sendEvent(createFlightSelectedEvent({ route: outboundRoute, type: FlightType.Outbound }))
    }
    if (inboundRoute) {
      analyticsClient.sendEvent(createFlightSelectedEvent({ route: inboundRoute, type: FlightType.Inbound }))
    }
  }

  async function getFlights(selectedRoomIds: string[] = [], selectedRoomContractIds: string[] = []) {
    const {
      setIn,
      accoId,
      contextItemId,
      bookingGateId,
      filterSelection,
      selectedPriceId,
      isAccommodationAvailableResponse,
    } = store.getState()

    setIn('getTransport.loading', true)
    try {
      const filtersCriteria = {
        contextItemId,
        bookingGateId,
        accoId,
        filterSelection,
        selectedPriceId: selectedPriceId || '',
        roomIds: isAccommodationAvailableResponse.isFlightOnly ? ['1XXX'] : selectedRoomIds,
        roomContractIds: selectedRoomContractIds,
      }
      const flightsResult = await flightSelectorApiClient.getFlights(filtersCriteria)
      setIn('transportResponse', flightsResult)
      setIn('outboundRoutes', flightsResult?.outboundRoutes || [])
      const selectedOutboundRouteId = flightsResult?.outboundRoutes[0]?.id || null
      if (selectedOutboundRouteId) {
        await setOutboundRouteId(selectedOutboundRouteId)
      }
      setIn('getTransport.loading', false)
    }
    catch (error) {
      setIn('getTransport.loading', false)
      setIn('getTransport.error', true)
      throw error
    }
  }

  async function getSelfDrive(selectedRoomIds: string[] = [], selectedRoomContractIds: string[] = []) {
    const { setIn, accoId, contextItemId, bookingGateId, filterSelection, selectedPriceId } = store.getState()
    setIn('getTransport.loading', true)
    try {
      const filtersCriteria = {
        contextItemId,
        bookingGateId,
        accoId,
        filterSelection,
        selectedPriceId: selectedPriceId || '',
        roomIds: selectedRoomIds,
        roomContractIds: selectedRoomContractIds,
      }
      const selfDriveResult = await selfDriveSelectorApiClient.getSelfDrive(filtersCriteria)
      setIn('selfDriveResponse', selfDriveResult)
      setIn('getTransport.loading', false)
    }
    catch (error) {
      setIn('getTransport.loading', false)
      setIn('getTransport.error', true)
      throw error
    }
  }

  async function clearStateForTransport() {
    const { setIn } = store.getState()
    setIn('transportResponse', emptyTransportResponse)
    setIn('selfDriveResponse', emptySelfDriveResponse)
    setIn('outboundRoutes', [])
    setIn('inboundRoutes', [])
    setIn('selectedOutboundRouteId', null)
    setIn('selectedInboundRouteId', null)
  }

  async function clearStateForRooms() {
    const { setIn } = store.getState()
    setIn('roomsResponse', emptyRoomsResponse)
    setIn('selectedRooms', {})
    setIn('selectedRoomInfos', [])
    await clearStateForTransport()
  }

  async function clearStateForPrices() {
    const { setIn } = store.getState()
    setIn('selectedPriceId', null)
    await clearStateForRooms()
  }

  async function clearStateForFilters() {
    await clearStateForPrices()
  }

  async function selectFilter({ type, selection }: { type: PricesFilterType, selection: FiltersInPricesValues }) {
    const { setIn, filterSelection } = store.getState()
    if (
      (filterSelection[type] === undefined && selection === undefined)
      || JSON.stringify(filterSelection[type] ?? []) === JSON.stringify(selection)
    ) {
      return
    }

    const newSelection = {
      ...filterSelection,
      [type]: selection,
    }
    setIn('filterSelection', newSelection)

    updateQueryString(store.getState())
    await clearStateForFilters()
    if (type === 'Participants') {
      await getFilters().then(() => getPrices())
    }
    else {
      await getPrices()
    }
  }

  async function selectPrice(priceId: string | null) {
    const { setIn } = store.getState()
    setIn('selectedPriceId', priceId)
    const { isFlightOnly } = isAccommodationAvailableSelector(store.getState())
    if (isFlightOnly) {
      await clearStateForTransport()
      await getFlights()
    }
    else {
      await clearStateForRooms()
      await getRooms()
    }
  }

  const sendParticipantsAllocatedEvent = () => {
    const { analytics, pricesResponse, selectedPriceId, filterSelection, selectedRoomInfos } = store.getState()
    if (!analytics)
      return

    const selectedPrice = pricesResponse.prices.find(price => price.id === selectedPriceId)

    const { TransportType, Participants } = filterSelection
    const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
      Participants || [],
    )

    analyticsClient.sendEvent(
      createParticipantsAllocatedEvent({
        analytics,
        transportType: TransportType ?? '',
        boardType: selectedPrice?.mealplan ?? '',
        duration: selectedPrice?.duration ? selectedPrice.duration.toString() : '',
        partyComposition,
        totalGuests,
        departureDate: selectedPrice?.departureDate ?? '',
        price: selectedPrice?.minPricePerPerson ?? 0,
        roomType: selectedRoomInfos.map(room => room?.name).join(' - '),
        campaignId: selectedPrice?.bullet?.code ?? '',
      }),
    )
  }

  async function selectRoom(roomNumber: string, roomId: string | null) {
    const { setIn, roomsResponse, selectedRooms } = store.getState()
    const { roomGroups } = roomsResponse
    const { selectedValue: transportType } = transportTypeFilterOptionsSelector(store.getState())
    const newSelectedRooms = {
      ...selectedRooms,
      [roomNumber]: roomId,
    }
    setIn('selectedRooms', newSelectedRooms)

    const allRoomsSelected
      = newSelectedRooms
        && Object.values(newSelectedRooms).filter(value => value !== null).length === roomGroups?.length

    const selectedRoomIds: string[] = []
    const selectedRoomContractIds: string[] = []
    const selectedRoomInfos: RoomInfo[] = []

    if (allRoomsSelected) {
      for (const [roomNumber, roomId] of Object.entries(newSelectedRooms)) {
        if (roomId !== null) {
          const roomToAdd = roomGroups
            ?.find(roomGroup => roomGroup.roomNumber === roomNumber)
            ?.rooms
            ?.find(room => room.id === roomId)
          if (roomToAdd) {
            selectedRoomIds.push(roomToAdd.id)
            selectedRoomContractIds.push(roomToAdd.contractId)
            selectedRoomInfos.push(roomToAdd)
          }
        }
      }
      setIn('selectedRoomInfos', selectedRoomInfos)
      sendParticipantsAllocatedEvent()
      if (transportType === 'Flight') {
        await getFlights(selectedRoomIds, selectedRoomContractIds)
      }
      else if (transportType === 'SelfDrive') {
        await getSelfDrive(selectedRoomIds, selectedRoomContractIds)
      }
    }
    else {
      await clearStateForTransport()
    }
  }

  return {
    initPrices,
    initPriceTable,
    selectFilter,
    selectPrice,
    selectRoom,
    setOutboundRouteId,
    setInboundRouteId,
  }
}
